import React from 'react'
import { loadComponentModule } from "components/ComponentType";
import ErrorBoundary from "../components/ErrorBoundary";
import {queryClient} from "services/HttpService";
import { QueryClientProvider } from 'react-query'
import {createRoot, Root} from 'react-dom/client';
import {useUserState} from "../store/user-store";

export const COMPONENT_ATTRIBUTE = 'jsComponent';
export const COMPONENT_SELECTOR  = '[jsComponent]';
export const PROPS_ATTRIBUTE     = 'props';
const COMPONENT_IS_ROOT_FLAG = "_isComponentRoot";


// Component map [id -> HTML Element]
interface IReactRootEl {
    el: Element;
    root: Root;
}

let rootMap = new Map<string, IReactRootEl>();

/**
 * Renders a component into a DOM element. This is main entry point for rendering components.
 * @param root
 * @param importPromise Promise that resolves to a React component
 * @param props Props to pass to the component
 */
const renderComponent = (root: Root, importPromise: any, props: any) => {
    // @ts-ignore
    importPromise.then(async (module) => {
        const component = () => <>
            <React.StrictMode>
                <QueryClientProvider client={queryClient} contextSharing={true} >
                    <ErrorBoundary>
                        {React.createElement(module.default, props)}
                    </ErrorBoundary>

                    {/*<ReactQueryDevtools initialIsOpen={false} />*/}
                </QueryClientProvider>
            </React.StrictMode>
        </>

        root.render(component());
    });
}


const mountElementRoot = (el: Element, elType?: string, elProps?: any) => {
    const id = el.id;
    const type = elType ?? el.getAttribute(COMPONENT_ATTRIBUTE);
    const props = elProps ?? parseJsonProps(el.getAttribute(PROPS_ATTRIBUTE)!);

    if(rootMap.has(id)){
        const oldRoot = rootMap.get(id);
        oldRoot!!.root.unmount();
        const oldType = oldRoot?.el.getAttribute(COMPONENT_ATTRIBUTE);

        console.log(`Re-mounted [${type}] component at [${id}]. Old component type [${oldType}].`);
    }else{
        console.log(`Mounting [${type}] component at [${id}].`)
    }

    // TODO: Handle webpack hot reload warnings
    const root = {el, root: createRoot(el, {identifierPrefix: id + '_'})};
    el.setAttribute(COMPONENT_IS_ROOT_FLAG, "true");

    rootMap.set(id, root);

    const importPromise = loadComponentModule(type!);

    if(importPromise){
        renderComponent(root.root, importPromise, props);
    }else{
        root.root.render(<MissingComponentWarning componentId={type}/>);
        console.log("Unknown component! " + type)
    }
}


const parseJsonProps = (jsonProps: string) => {
    try {
        return JSON.parse(jsonProps);
    } catch {
        return {};
    }
}

interface MissingComponentWarningProps {
    componentId: string | null
}

const MissingComponentWarning = ({ componentId }: MissingComponentWarningProps) => {
    const icon = <i className="pi pi-times" style={{color: "#f6e574", fontSize: "3rem"}}/>
    let errorMsg: string
    if(componentId != null) {
        errorMsg = `Invalid component configuration: component type [${componentId}] does not exit!`;
    } else {
        errorMsg = `Invalid component configuration: component type is missing!`;
    }

    return <h1 style={{display: "flex", alignItems: "center", justifyContent: "center"}}>{icon}{errorMsg}{icon}</h1>;
}

export const initEasasJs = () => {
    let els = Array.from(document.querySelectorAll(COMPONENT_SELECTOR));

    const renderCallback = () => {
        if(unsub != undefined) unsub()

        els.forEach(el => {
            mountElementRoot(el);
        });
    }


    let unsub: () => void
    if(useUserState.getState().isCurrentUserLoaded) {
        renderCallback();
    } else {
        unsub = useUserState.subscribe(renderCallback);
    }


    window.EASAS = {
        mountRoot: mountElementRoot,
    }
}